import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CaretRight, Gift, QrCode, Storefront, MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import { pts, initials } from "@/lib/format";

export default function CustomerBusinesses() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    setList(null);
    api.get("/customer/businesses").then((r) => setList(r.data)).catch(() => setError(true));
  };

  useEffect(() => { load(); }, []);

  if (error)
    return (
      <div className="pt-2 space-y-4">
        <h1 className="font-display font-bold text-2xl">My Businesses</h1>
        <div className="card-surface p-8 text-center" data-testid="businesses-error">
          <p className="font-display font-semibold text-lg mb-1">Couldn't load your businesses</p>
          <p className="text-sm text-muted-foreground mb-5">Please check your connection and try again.</p>
          <button data-testid="businesses-retry" onClick={load} className="inline-flex items-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 h-11">
            Retry
          </button>
        </div>
      </div>
    );

  if (!list) return <div className="pt-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="pt-2 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-2xl">My Businesses</h1>
        <Link to="/app/discover" data-testid="discover-link" className="flex items-center gap-1 text-sm text-primary font-medium">
          <MagnifyingGlass size={16} /> Discover
        </Link>
      </div>
      {list.length === 0 && (
        <div className="card-surface p-8 text-center" data-testid="businesses-empty">
          <div className="h-14 w-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-4">
            <Storefront size={26} weight="duotone" className="text-primary" />
          </div>
          <p className="font-display font-semibold text-lg mb-1">No businesses yet</p>
          <p className="text-sm text-muted-foreground max-w-xs mx-auto">
            You join a business automatically the first time it scans your OneKite QR and awards you points.
          </p>
          <Link to="/app/qr" data-testid="empty-show-qr" className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 h-11 transition-all active:scale-95">
            <QrCode size={18} weight="fill" /> Show my QR
          </Link>
          <Link to="/app/discover" data-testid="empty-discover" className="mt-3 block text-sm text-primary font-medium">
            See participating businesses
          </Link>
        </div>
      )}
      <div className="space-y-3">
        {list.map((b, i) => (
          <button key={b.business_id} data-testid="business-card" onClick={() => nav(`/app/businesses/${b.business_id}`)}
            className="w-full text-left card-surface p-4 flex items-center gap-4 hover:-translate-y-0.5 transition-transform fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center overflow-hidden shrink-0">
              {b.logo ? <img src={b.logo} alt="" className="h-full w-full object-cover" /> : <span className="font-display font-bold text-primary">{initials(b.name)}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{b.name}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <span className="text-primary font-mono font-semibold">{pts(b.points)} pts</span>
                {b.rewards_available > 0 && (
                  <span className="flex items-center gap-1 text-primary"><Gift size={12} weight="fill" /> {b.rewards_available} reward{b.rewards_available > 1 ? "s" : ""}</span>
                )}
              </p>
            </div>
            <CaretRight size={18} className="text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}
